// src/features/clanWatches/ClanWatchesPage.tsx
import { useState, type FormEvent } from 'react';
import { useGuild } from '../../lib/GuildContext';
import { formatGuildName } from '../../lib/formatGuildName';
import {
  useClanWatches, useCreateClanWatch, useUpdateClanWatch, useDeleteClanWatch,
  type ClanWatch, type ClanWatchInput,
} from './useClanWatches';
import { ChannelPicker } from '../../components/pickers/ChannelPicker';
import './ClanWatchesPage.css';

function WatchForm({
  initial, onSubmit, onCancel, submitting, submitLabel, guildId,
}: {
  initial: ClanWatchInput;
  onSubmit: (input: ClanWatchInput) => void;
  onCancel?: () => void;
  submitting: boolean;
  submitLabel: string;
  guildId: string | null;
}) {
  const [clanName, setClanName] = useState(initial.clanName);
  const [vault, setVault] = useState(initial.vaultChannelId ?? '');
  const [events, setEvents] = useState(initial.eventsChannelId ?? '');
  const [other, setOther] = useState(initial.otherChannelId ?? '');

  const valid = clanName.trim().length > 0 && clanName.length <= 32;

  function handleSubmit(e: FormEvent) {
    e.preventDefault();
    if (!valid) return;
    onSubmit({
      clanName: clanName.trim(),
      vaultChannelId: vault.trim() || null,
      eventsChannelId: events.trim() || null,
      otherChannelId: other.trim() || null,
    });
  }

  return (
    <form onSubmit={handleSubmit} className="clan-watch-form">
      <div className="clan-watch-form__field">
        <label>Clan name</label>
        <input className="input" value={clanName} onChange={(e) => setClanName(e.target.value)} maxLength={32} />
      </div>
      <div className="clan-watch-form__field">
        <label>Vault log channel (optional)</label>
        <ChannelPicker guildId={guildId} value={vault} onChange={setVault} placeholder="Channel ID" />
      </div>
      <div className="clan-watch-form__field">
        <label>Events log channel (optional)</label>
        <ChannelPicker guildId={guildId} value={events} onChange={setEvents} placeholder="Channel ID" />
      </div>
      <div className="clan-watch-form__field">
        <label>Other log channel (optional)</label>
        <ChannelPicker guildId={guildId} value={other} onChange={setOther} placeholder="Channel ID" />
      </div>
      <div className="clan-watch-form__actions">
        <button className="button-primary" type="submit" disabled={!valid || submitting}>
          {submitting ? 'Saving…' : submitLabel}
        </button>
        {onCancel && <button className="button-secondary" type="button" onClick={onCancel}>Cancel</button>}
      </div>
    </form>
  );
}

function WatchRow({ watch, guildId }: { watch: ClanWatch; guildId: string | null }) {
  const [editing, setEditing] = useState(false);
  const [confirmingDelete, setConfirmingDelete] = useState(false);
  const update = useUpdateClanWatch(guildId);
  const del = useDeleteClanWatch(guildId);

  function handleDelete() {
    if (!confirmingDelete) { setConfirmingDelete(true); return; }
    del.mutate(watch.id);
  }

  if (editing) {
    return (
      <div className="clan-watch-row">
        <WatchForm
          guildId={guildId}
          initial={{
            clanName: watch.clan_name,
            vaultChannelId: watch.vault_channel_id,
            eventsChannelId: watch.events_channel_id,
            otherChannelId: watch.other_channel_id,
          }}
          submitting={update.isPending}
          submitLabel="Save changes"
          onCancel={() => setEditing(false)}
          onSubmit={(input) => update.mutate({ id: watch.id, input }, { onSuccess: () => setEditing(false) })}
        />
      </div>
    );
  }

  return (
    <div className="clan-watch-row">
      <div>
        <p className="clan-watch-row__clan">{watch.clan_name}</p>
        <p className="clan-watch-row__channels">
          {watch.vault_channel_id && <>Vault: {watch.vault_channel_id} </>}
          {watch.events_channel_id && <>· Events: {watch.events_channel_id} </>}
          {watch.other_channel_id && <>· Other: {watch.other_channel_id}</>}
          {!watch.vault_channel_id && !watch.events_channel_id && !watch.other_channel_id && 'No log channels set'}
        </p>
      </div>
      <div className="clan-watch-row__actions">
        <button className="button-secondary" onClick={() => setEditing(true)}>Edit</button>
        <button className="button-danger" onClick={handleDelete} disabled={del.isPending}>
          {confirmingDelete ? 'Click again to confirm' : 'Delete'}
        </button>
      </div>
    </div>
  );
}

export function ClanWatchesPage() {
  const { guilds, selectedGuildId } = useGuild();
  const guild = guilds.find((g) => g.guild_id === selectedGuildId);
  const { data: watches, isLoading, error } = useClanWatches(selectedGuildId);
  const create = useCreateClanWatch(selectedGuildId);
  const [showCreate, setShowCreate] = useState(false);

  return (
    <>
      <div className="page-header">
        <p className="page-header__eyebrow">{guild ? formatGuildName(guild.guild_name, guild.guild_id) : 'No server selected'}</p>
        <h1 className="page-header__title">Clan Watches</h1>
      </div>

      <article className="card">
        <header className="card__header">
          <div>
            <p className="card__eyebrow">{watches?.length ?? 0} watched</p>
            <h2 className="card__title">Watched Clans</h2>
          </div>
          {!showCreate && (watches?.length ?? 0) < 3 && <button className="button-primary" onClick={() => setShowCreate(true)}>Add clan</button>}
        </header>
        <div className="card__content">
          {(watches?.length ?? 0) >= 3 && !showCreate && (
            <p style={{ color: 'var(--text-muted)', fontSize: 13 }}>
              Maximum of 3 watched clans per server reached. Remove one below to add another.
            </p>
          )}
          {showCreate && (
            <div className="clan-watch-row">
              <WatchForm
                guildId={selectedGuildId}
                initial={{ clanName: '', vaultChannelId: null, eventsChannelId: null, otherChannelId: null }}
                submitting={create.isPending}
                submitLabel="Add clan"
                onCancel={() => setShowCreate(false)}
                onSubmit={(input) => create.mutate(input, { onSuccess: () => setShowCreate(false) })}
              />
            </div>
          )}

          {isLoading && <p style={{ color: 'var(--text-muted)', fontSize: 13 }}>Loading…</p>}
          {error && <p style={{ color: 'var(--negative)', fontSize: 13 }}>Couldn&apos;t load clan watches.</p>}
          {watches?.length === 0 && !showCreate && <p style={{ color: 'var(--text-muted)', fontSize: 13 }}>No clans being watched yet.</p>}

          {watches?.map((watch) => <WatchRow key={watch.id} watch={watch} guildId={selectedGuildId} />)}
        </div>
      </article>
    </>
  );
}
